import AppIconButton from '@nxsys/components/AppIconButton';
import {
  defaultPaymentFrequency,
  defaultSubcontractorStatusOption,
} from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { ColumnsType } from 'antd/es/table';
import { find } from 'lodash';
import moment from 'moment';
import { formatFullName } from '@nxsys/utils';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { IRELAND } from '@nxsys/store/constants/keys';

export const getOnHoldWorkerColumns = ({
  messages,
  onView,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  onView: (record: EmployeeRecordType) => void;
  payrollCountry?: string;
  isBureau?: boolean;
}): ColumnsType<EmployeeRecordType> => {
  const isIreland = payrollCountry === IRELAND;
  return [
    {
      key: 'actions',
      title: messages['common.action'],
      align: 'left',
      width: 100,
      fixed: 'left',
      render: (_: unknown, record: EmployeeRecordType) => (
        <ul className="table-actions flex items-center justify-start">
          <li>
            <AppIconButton
              title={messages['common.edit']}
              icon={<Icons.IconEdit />}
              onClick={() => onView(record)}
            />
          </li>
        </ul>
      ),
    },
    {
      key: 'status',
      title: messages['common.status'],
      align: 'center',
      width: 140,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) =>
        find(
          defaultSubcontractorStatusOption,
          (item) => item.value === record.status,
        )?.label ||
        record.status ||
        '_',
    },
    {
      key: 'id',
      title: messages['common.systemId'],
      width: 180,
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value || '_',
    },
    {
      key: 'name',
      title: messages['table.employeeName'],
      width: 240,
      render: (record) => formatFullName(record) || '_',
    },
    {
      key: 'email',
      title: messages['common.emailId'],
      width: 220,
      dataIndex: 'email',
      render: (value) => value || '_',
    },
    {
      key: 'mobile',
      title: messages['common.mobile'],
      width: 180,
      dataIndex: 'mobile',
      render: (value) => value || '_',
    },
    {
      key: 'employer',
      title: isBureau ? (messages['table.employer'] || 'Employer') : (messages['table.paymentCompany'] || 'Employer Name'),
      width: 240,
      render: (record) => record?.companyPayment?.companyName || '_',
    },
    {
      key: 'registrationStatus',
      title: messages['common.registrationStatus'],
      align: 'center',
      width: 200,
      dataIndex: 'registrationStatus',
      render: (value) => value || '_',
    },
    {
      key: 'payFrequency',
      title: messages['common.payFrequency'],
      align: 'center',
      width: 180,
      dataIndex: 'payFrequency',
      render: (value) =>
        find(defaultPaymentFrequency, (item) => item.value === value)?.label ||
        value ||
        '_',
    },
    {
      key: 'NINumber',
      title: getFieldLabelByCountry(payrollCountry ?? '', 'NINo'),
      width: 180,
      dataIndex: 'NINumber',
      render: (value) => value || '_',
    },
    ...(!isIreland ? [{
      key: 'niCategory',
      title: messages['common.niCategory'] || 'NI Category',
      width: 160,
      render: (record: EmployeeRecordType) => record?.setting?.niCategory || '_',
    },
    {
      key: 'taxCode',
      title: messages['common.taxCode'],
      width: 140,
      render: (record: EmployeeRecordType) => record?.setting?.taxCode || '_',
    }] as ColumnsType<EmployeeRecordType> : []),
    {
      key: 'lastPaidDate',
      title: messages['common.lastPaidDate'] || 'Last Paid Date',
      align: 'right',
      width: 180,
      render: (record) =>
        record?.latestPayroll?.paymentDate
          ? moment(record.latestPayroll.paymentDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'lastPaidPeriod',
      title: messages['common.lastPaidPeriod'] || 'Last Paid Period',
      align: 'right',
      width: 200,
      render: (record) => {
        if (
          record?.latestPayroll?.taxPeriod &&
          record?.latestPayroll?.taxYear
        ) {
          return `${record.latestPayroll.taxPeriod}/${record.latestPayroll.taxYear}`;
        }
        return '_';
      },
    },
  ];
};
